import { type FC, useCallback, useContext, useState } from "react";

import {
  Button,
  Checkbox,
  CheckboxState,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { UserActions } from "~/v2/api/auth";
import { getAuthRedirectReason } from "~/v2/api/auth/authFailure";
import { getAccessToken } from "~/v2/api/auth/authxToken";
import { OTPInput } from "~/v2/components/common";
import { sleep } from "~/v2/helpers";
import { withAppBasePath } from "~/v2/utils/appBasePath";
import { AuthContext, AuthState } from "../AuthContext";
import { DEFAULT_PATHS } from "./Login";

export const MFA: FC = () => {
  const { cognitoUser, setCognitoUser, setAuthState } = useContext(AuthContext);

  const [code, setCode] = useState("");
  const [lastResend, setLastResend] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | "neutral">(
    "neutral",
  );
  const [rememberDevice, setRememberDevice] = useState<CheckboxState>(
    CheckboxState.Checked,
  );

  const onRememberDeviceChange = (state: CheckboxState) =>
    setRememberDevice(
      state === CheckboxState.Indeterminate ? CheckboxState.Unchecked : state,
    );

  const isSMS = cognitoUser?.challengeName === "SMS_MFA";

  const onSubmit = useCallback(
    async (submittedCode: string) => {
      if (isVerifying) {
        return;
      }

      if (submittedCode.length !== 6) {
        setStatus("error");
        toastError("Please fill in all fields");
        return;
      }

      setIsVerifying(true);
      await sleep(500);

      if (!cognitoUser) {
        setStatus("error");
        toastError("User not available");
        setIsVerifying(false);
        return;
      }

      try {
        await UserActions.confirmMultiFactorAuth(
          cognitoUser,
          submittedCode,
          cognitoUser.challengeName === "SMS_MFA" ||
            cognitoUser.challengeName === "SOFTWARE_TOKEN_MFA"
            ? cognitoUser.challengeName
            : null,
        );

        if (rememberDevice === CheckboxState.Checked) {
          await UserActions.rememberDevice();
        }

        // Ensure the Cognito session is fully persisted to cookie storage
        // before exchanging the token, so authx can read the current session.
        await UserActions.getCurrentUser();

        try {
          // Exchange before marking login successful so failures do not leave
          // the OAuth flow in a partially authenticated state.
          await getAccessToken();
        } catch (err) {
          setCognitoUser(undefined);
          await UserActions.userLogout(
            { global: false },
            getAuthRedirectReason(err),
          );
          throw err;
        }

        toastSuccess("You are now logged in");
        setStatus("success");

        window.sessionStorage?.setItem("oauth_login", "1");

        await sleep(1500);

        const requestedPathVal =
          window.sessionStorage?.getItem("requested_path") || "";

        window.location.href = withAppBasePath(
          DEFAULT_PATHS.includes(requestedPathVal) ? "/" : requestedPathVal,
        );
      } catch (err) {
        setStatus("error");

        const errorMessage =
          err instanceof Error
            ? err.message
            : "An unexpected error has occurred.";

        toastError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    },
    [cognitoUser, isVerifying, rememberDevice, setCognitoUser],
  );

  const handleCancel = () => {
    setAuthState(AuthState.LOGIN);
    setCognitoUser(undefined);
  };

  const handleRecovery = () => {
    setAuthState(AuthState.MFA_RECOVERY);
  };

  const handleResend = () => {
    if (!cognitoUser) {
      toastError("User not available");
      return;
    }

    const now = Date.now();
    const msSinceLastResend = now - lastResend;
    const throttleLimit = 5000;

    if (msSinceLastResend < throttleLimit) {
      const secondsLeft = Math.ceil((throttleLimit - msSinceLastResend) / 1000);
      toastError(`Please wait ${secondsLeft} more second(s) before resending.`);
      return;
    }

    setLastResend(now);

    cognitoUser.resendConfirmationCode((err) => {
      const message = err ? err.message : "Code resent successfully";
      const toastFunction = err ? toastError : toastSuccess;
      toastFunction(message);
    });
  };

  return (
    <>
      <OTPInput
        onChange={(c) => {
          setCode(c);
          setStatus("neutral");
        }}
        onComplete={onSubmit}
        status={status}
        disabled={isVerifying}
      />
      <div className="flex w-full flex-col gap-6">
        <div>
          <label className="flex">
            <span className="mr-4 block text-sm">Remember this device?</span>
            <Checkbox
              state={rememberDevice}
              onChange={onRememberDeviceChange}
            />
          </label>
          <Text className="font-bold text-xs">
            Uncheck if using a shared device
          </Text>
        </div>

        <div className="flex space-x-2">
          <Button
            className="flex-1"
            variant="v2/stroke"
            type="button"
            onClick={handleCancel}
            data-testid="mfa-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="v2/filled"
            className="flex-1"
            loading={isVerifying}
            disabled={isVerifying}
            onClick={() => onSubmit(code)}
            data-testid="mfa-submit-button"
          >
            Verify
          </Button>
        </div>
        <div className="text-center text-sm">
          {isSMS ? (
            <>
              A code was sent via SMS to{" "}
              {cognitoUser?.challengeParam?.CODE_DELIVERY_DESTINATION}.{" "}
              <div
                className="inline-block cursor-pointer font-medium text-zinc-900 underline hover:text-zinc-500"
                onClick={handleResend}
              >
                Resend?
              </div>
            </>
          ) : (
            <>Find the code in your authenticator app.</>
          )}
          <br />
          <div
            className="inline-block cursor-pointer font-medium text-zinc-900 underline hover:text-zinc-500"
            onClick={handleRecovery}
            data-testid="mfa-recovery-link"
          >
            Use recovery code?
          </div>
        </div>
      </div>
    </>
  );
};
